import React from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Divider,
  Tooltip,
  useTheme,
  Alert,
  Snackbar
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import MessageIcon from '@mui/icons-material/Message';
import CallRounded from '@mui/icons-material/CallRounded';
import SmsRounded from '@mui/icons-material/SmsRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import { sendMessage } from '../services/bookingService';
import { isBackgroundDark } from '../services/themeBuilder';

interface BusinessContactSectionProps {
  businessId: string;
  contactName: string;
  phoneNumber: string;
}

export const BusinessContactSection: React.FC<BusinessContactSectionProps> = ({
  businessId,
  contactName,
  phoneNumber
}) => {
  const theme = useTheme();
  const isDarkBg = isBackgroundDark(theme.palette.background.default);
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);

  const phoneProvided = Boolean(phoneNumber?.trim());
  const nameError = name !== '' && name.trim().length < 2;
  const emailError = email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const messageError = message !== '' && message.trim().length < 4;

  const canSubmit = !nameError && !emailError && !messageError && name && email && message;

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(phoneNumber || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || sending) return;
    
    setSending(true);
    try {
      await sendMessage({
        businessId,
        senderName: name,
        senderEmail: email,
        senderMessage: message
      });
      
      // Clear form and show success
      setName('');
      setEmail('');
      setMessage('');
      setShowSuccess(true);
    } catch (error) {
      console.error('Failed to send message:', error);
      setShowError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box id="contact" sx={{ scrollMarginTop: 80 }}>
      <Box maxWidth="1100px" mx="auto" px={{ xs: 2, sm: 3 }} py={{ xs: 3, sm: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 800, 
            mb: 2,
            color: isDarkBg ? '#FFFFFF' : 'text.primary'
          }}
        >
          Contact
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Contact Card */}
          <Card
            variant="outlined"
            sx={{
              flex: { md: '0 0 38%' },
              borderRadius: 3,
              borderColor: theme.palette.divider,
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack spacing={1.25} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body1">
                    <strong>Owner:</strong> {contactName || 'Not provided'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body1">
                    <strong>Phone:</strong>{' '}
                    {phoneProvided ? (
                      <Box
                        component="a"
                        href={`tel:${phoneNumber}`}
                        sx={{ 
                          color: 'primary.main', 
                          textDecoration: 'none', 
                          fontWeight: 600,
                          '&:hover': {
                            color: 'accent.main',
                          }
                        }}
                      >
                        {phoneNumber}
                      </Box>
                    ) : (
                      'Not provided'
                    )}
                  </Typography>
                </Stack>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<CallRounded />}
                  href={phoneProvided ? `tel:${phoneNumber}` : undefined}
                  disabled={!phoneProvided}
                >
                  Call
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  startIcon={<SmsRounded />}
                  href={phoneProvided ? `sms:${phoneNumber}` : undefined}
                  disabled={!phoneProvided}
                >
                  Text
                </Button>
                <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                  <span>
                    <IconButton
                      color="default"
                      onClick={copyPhone}
                      disabled={!phoneProvided}
                      sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
                      aria-label="Copy phone number"
                    >
                      <ContentCopyRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </CardContent>
          </Card>

          {/* Message Form */}
          <Card variant="outlined" sx={{ flex: 1, borderRadius: 3, borderColor: theme.palette.divider }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: 'secondary.main' }}>
                Send a Message
              </Typography>

              <Box component="form" onSubmit={onSubmit}
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
                }}
              >
                <TextField
                  fullWidth
                  label="Your Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                  required
                  error={nameError}
                  helperText={nameError ? 'Please enter at least 2 characters.' : ' '}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  autoComplete="name"
                />

                <TextField
                  fullWidth
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                  required
                  error={emailError}
                  helperText={emailError ? 'Enter a valid email address.' : ' '}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  autoComplete="email"
                />

                <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                  <TextField
                    fullWidth
                    label="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="How can we help?"
                    multiline
                    minRows={4}
                    required
                    error={messageError}
                    helperText={messageError ? 'Please enter a bit more detail.' : ' '}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MessageIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!canSubmit || sending}
                    fullWidth
                  >
                    {sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>
      
      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Message sent successfully! We'll get back to you soon.
        </Alert>
      </Snackbar>
      
      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          Failed to send message. Please try again or contact us directly.
        </Alert>
      </Snackbar>
    </Box>
  );
};
